import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider } from './context/AuthContext';
import ProtectedRoute from './components/auth/ProtectedRoute';
import { Analytics } from "@vercel/analytics/react";
import { SpeedInsights } from "@vercel/speed-insights/react";

// 1. IMPORT TANSTACK QUERY ASSETS
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';

// Components & Structural Base Shell Layouts
import SplashScreen from './components/splash/SplashScreen';
import DashboardLayout from './components/dashboard/DashboardLayout';

// Public Pages
import Login from './pages/Login';
import Signup from './pages/Signup';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import TermsOfService from './pages/TermsOfService';
import PrivacyPolicy from './pages/PrivacyPolicy';
import HowItWorks from './pages/HowItWorks';

// Protected Pages
import Dashboard from './pages/Dashboard';
import Upload from './pages/Upload';
import Pricing from './pages/Pricing';
import DocumentView from './pages/DocumentView';
import DocumentHistory from './pages/DocumentHistory';
import Settings from './pages/Settings';
import PastQuestions from './pages/PastQuestions';
import { ROUTES } from './lib/constants';
import './App.css';

// Global error handler to catch repetitive browser ResizeObserver limitations
window.addEventListener('error', (e) => {
  if (e.message === 'ResizeObserver loop completed with undelivered notifications.' || 
      e.message === 'ResizeObserver loop limit exceeded') {
    const resizeObserverErrGuid = '873639a9-ad0d-4bcd-90c2-8001d8e8b23e';
    if (window.name !== resizeObserverErrGuid) {
      window.name = resizeObserverErrGuid;
      e.stopImmediatePropagation();
    }
  }
});

// 2. CONFIGURE THE CENTRAL CACHE CLIENT
// Keeping this outside the component prevents it from re-instantiating on render loops
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes: Caches textbooks/summaries so returning to tabs is instant
      refetchOnWindowFocus: false, // Stops background spamming when students switch browser tabs during exams
    },
  },
});

function App() {
  return (
    // 3. WRAP YOUR APP PROVIDER TREE
    <QueryClientProvider client={queryClient}>
      <Router>
        <AuthProvider>
          <Analytics />
          <SpeedInsights />
          <Toaster 
            position="top-right"
            toastOptions={{
              duration: 3000,
              style: {
                background: '#faf8f5',
                color: '#6b5a48',
                border: '1px solid #e3d5c3',
                borderRadius: '12px',
                padding: '12px 16px',
                fontSize: '14px',
                fontWeight: '500',
              },
              success: {
                iconTheme: {
                  primary: '#22c55e',
                  secondary: '#faf8f5',
                },
              },
              error: {
                iconTheme: {
                  primary: '#ef4444',
                  secondary: '#faf8f5',
                },
              },
            }}
          />
          
          <Routes>
            {/* =========================================================
                1. PUBLIC MARKETING & ONBOARDING GATEWAY ROUTES
               ========================================================= */}
            <Route path="/" element={<SplashScreen />} />
            <Route path={ROUTES.LOGIN} element={<Login />} />
            <Route path={ROUTES.SIGNUP} element={<Signup />} />
            <Route path={ROUTES.FORGOT_PASSWORD} element={<ForgotPassword />} />
            <Route path={ROUTES.RESET_PASSWORD} element={<ResetPassword />} />
            <Route path={ROUTES.TERMS} element={<TermsOfService />} />
            <Route path={ROUTES.PRIVACY} element={<PrivacyPolicy />} />
            <Route path={ROUTES.HOW_IT_WORKS} element={<HowItWorks />} />

            {/* =========================================================
                2. PROTECTED ROUTE SUB-TREE (Wrapped inside Layout Frame)
               ========================================================= */}
            <Route
              element={
                <ProtectedRoute>
                  <DashboardLayout />
                </ProtectedRoute>
              }
            >
              {/* Pages automatically inherit DashboardHeader + Bottom Nav layout configurations */}
              <Route path={ROUTES.DASHBOARD} element={<Dashboard />} />
              <Route path={ROUTES.UPLOAD} element={<Upload />} />
              <Route path={ROUTES.HISTORY} element={<DocumentHistory />} />
              <Route path={ROUTES.SETTINGS} element={<Settings />} />
              <Route path={ROUTES.PAST_QUESTIONS} element={<PastQuestions />} />
              <Route path={ROUTES.PRICING} element={<Pricing />} />
              <Route path={ROUTES.DOCUMENT} element={<DocumentView />} />
            </Route>

            {/* Fallback Redirection */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </AuthProvider>
      </Router>
    </QueryClientProvider>
  );
}

export default App;